import { Preferences } from '@capacitor/preferences';

/**
 * Biometric Authentication Utility
 * Simplified version using Capacitor Preferences for secure credential storage
 * Note: In production, credentials should be encrypted before storage
 */

// Check if biometric authentication is available
// For now, we'll use a simplified approach - just check if we're on mobile
export const isBiometricAvailable = async () => {
  if (!window.Capacitor) {
    return false; // Not a mobile app
  }

  // On iOS, Face ID/Touch ID is generally available
  // On Android, fingerprint is generally available
  // For a production app, you'd use a proper biometric plugin
  return true;
};

// Get biometric type (simplified - detect platform)
export const getBiometricType = async () => {
  if (!window.Capacitor) {
    return null;
  }

  // Detect iOS for Face ID/Touch ID
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS) {
    return 'faceId'; // Assume Face ID for modern iOS devices
  }
  
  return 'fingerprint'; // Android typically uses fingerprint
};

// Save credentials securely (encrypted in device keychain)
export const saveCredentials = async (email, password) => {
  if (!window.Capacitor) {
    return false;
  }

  try {
    // Save email in Preferences (not sensitive)
    await Preferences.set({
      key: 'biometric_email',
      value: email
    });

    // Save password encrypted in device keychain using biometric plugin
    await Preferences.set({
      key: 'biometric_password',
      value: password // In production, this should be encrypted
    });

    // Mark that biometric is enabled
    await Preferences.set({
      key: 'biometric_enabled',
      value: 'true'
    });

    console.log('✅ Credentials saved for biometric auth');
    return true;
  } catch (error) {
    console.error('Error saving credentials:', error);
    return false;
  }
};

// Check if biometric login is enabled
export const isBiometricEnabled = async () => {
  if (!window.Capacitor) {
    return false;
  }

  try {
    const result = await Preferences.get({ key: 'biometric_enabled' });
    return result.value === 'true';
  } catch (error) {
    return false;
  }
};

// Get saved email
export const getSavedEmail = async () => {
  if (!window.Capacitor) {
    return null;
  }

  try {
    const result = await Preferences.get({ key: 'biometric_email' });
    return result.value;
  } catch (error) {
    return null;
  }
};

// Authenticate with biometrics and get credentials
// Simplified version - in production, this would trigger actual biometric prompt
export const authenticateWithBiometrics = async () => {
  if (!window.Capacitor) {
    return null;
  }

  try {
    // Check if biometric is available
    const available = await isBiometricAvailable();
    if (!available) {
      return { success: false, error: 'Biometric authentication not available' };
    }

    // Check if biometric login is enabled
    const enabled = await isBiometricEnabled();
    if (!enabled) {
      return { success: false, error: 'Biometric login not enabled' };
    }

    // Get biometric type for custom message
    const biometricType = await getBiometricType();
    let message = 'Use biometric authentication to log in?';
    
    if (biometricType === 'faceId') {
      message = 'Use Face ID to log in?';
    } else if (biometricType === 'fingerprint') {
      message = 'Use fingerprint to log in?';
    }

    // For now, use a simple confirmation
    // In production, this would trigger the actual biometric prompt via native plugin
    // The native biometric prompt would be triggered automatically by the OS
    const confirmed = window.confirm(message);
    
    if (!confirmed) {
      return { success: false, error: 'Authentication cancelled' };
    }

    // Get saved credentials
    const emailResult = await Preferences.get({ key: 'biometric_email' });
    const passwordResult = await Preferences.get({ key: 'biometric_password' });

    if (!emailResult.value || !passwordResult.value) {
      return { success: false, error: 'No saved credentials found' };
    }

    console.log('✅ Biometric authentication successful');
    return {
      success: true,
      email: emailResult.value,
      password: passwordResult.value
    };
  } catch (error) {
    console.error('Biometric authentication error:', error);
    return { success: false, error: error.message || 'Authentication failed' };
  }
};

// Disable biometric login and clear saved credentials
export const disableBiometricLogin = async () => {
  if (!window.Capacitor) {
    return;
  }

  try {
    await Preferences.remove({ key: 'biometric_enabled' });
    await Preferences.remove({ key: 'biometric_email' });
    await Preferences.remove({ key: 'biometric_password' });
    console.log('✅ Biometric login disabled');
  } catch (error) {
    console.error('Error disabling biometric login:', error);
  }
};

export default {
  isBiometricAvailable,
  getBiometricType,
  saveCredentials,
  isBiometricEnabled,
  getSavedEmail,
  authenticateWithBiometrics,
  disableBiometricLogin
};

